import { useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import { Slider } from "./../Slider/index";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Loader } from "../Loader";
import { H2 } from "../Heading";
import { Paragraph } from "../Paragraph";
import downloadIcon from "../../assets/download-blue.svg";
import noDocuments from "../../assets/no-documents.svg";
import { Button } from "../Button";
import { Image } from "../Image";
import { useAuth } from "../../context/AuthContext";
import { notify } from "../../utils/notify";
import { DocumentList, DocumentListItem } from "../DocumentList";
import {
  downloadDocument,
  downloadDocuments,
} from "../../utils/downloadDocument";
import { NewDocumentsRequest } from "../../pages/IndividualView/NewDocumentsRequest";

/**
 * Uploaded Documents panel.
 *
 * Previously the row markup was written out twice (once per filter branch),
 * each row stacking three labelled blocks whose labels rendered heavier than
 * their values, with the filename repeated as both the row heading and a
 * "File name" field. Both branches now render through the shared DocumentList.
 *
 * The panel is a fixed width rather than 70% of the viewport: the content is a
 * single-column list, so a percentage width made it a near-takeover on a large
 * monitor while leaving the record underneath unreadable. 720px is set by what
 * a row needs, the filename column plus a 140px timestamp and the actions, and
 * still leaves the record readable beside it. The header is sticky so the
 * filter and Download All stay put while the list scrolls.
 */

const CATEGORIES = [
  { value: "proof_of_address", label: "Proof Of Address" },
  { value: "source_of_funds", label: "Source Of Funds" },
  { value: "extra_documents", label: "Extra Documents" },
  { value: "additional_documents", label: "Additional Documents" },
  { value: "forms", label: "Forms" },
] as const;

type CategoryKey = (typeof CATEGORIES)[number]["value"];
type FilterKey = "all" | CategoryKey;

const PanelHeader = styled(Box)`
  ${({ theme: { colors, space } }) => `
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${colors.white};
    padding: ${space[2]} ${space[3]};
    border-bottom: 1px solid ${colors.gray[100]};
  `}
`;

/**
 * Counted filter pills. This replaces a 300px Select whose counts were built
 * inside getOptionLabel: with six categories the counts are more useful all
 * visible at once than one at a time behind a dropdown.
 */
const FilterPill = styled.button<{ isActive: boolean }>`
  ${({ theme: { colors, radii, fontWeights }, isActive }) => `
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 10px;
    border-radius: ${radii[0]};
    border: 1px solid ${isActive ? colors.blue.primary : colors.gray[100]};
    background: ${isActive ? colors.blue[50] : colors.white};
    color: ${isActive ? colors.blue.primary : colors.gray[60]};
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: ${fontWeights.medium};
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      border-color: ${isActive ? colors.blue.primary : colors.gray[30]};
    }
  `}
`;

const PillCount = styled.span<{ isActive: boolean }>`
  ${({ theme: { colors, radii }, isActive }) => `
    min-width: 18px;
    padding: 0 4px;
    border-radius: ${radii[0]};
    background: ${isActive ? colors.blue.primary : colors.gray[10]};
    color: ${isActive ? colors.white : colors.gray[60]};
    font-size: 11px;
    line-height: 16px;
    text-align: center;
  `}
`;

export const AdditionalDocuments = ({
  isOpen,
  onRequestClose,
  documents,
  documentsLoading,
  includedAdditionalDocument,
  mandateId,
  individualId,
  documentType = "individual",
}) => {
  const { authHeader } = useAuth();
  const resolvedDocumentType =
    documentType === "entity" ? "entity" : "individual";

  const [filter, setFilter] = useState<FilterKey>("all");
  const [isSelectAdditionalDocumentOpen, setIsSelectAdditionalDocumentOpen] =
    useState(false);

  /** One entry per category, each already mapped onto the shared list shape. */
  const byCategory = useMemo(() => {
    const toItem = (document: any, index: number): DocumentListItem => ({
      id: document.id ?? document.media ?? `${document.name}-${index}`,
      fileName: document.name,
      media: document.media,
      description: document.description,
      uploadedOn: document.created_on,
      raw: document,
    });

    return CATEGORIES.reduce((acc, { value }) => {
      const rows = documents?.data?.[value];
      acc[value] = Array.isArray(rows) ? rows.map(toItem) : [];
      return acc;
    }, {} as Record<CategoryKey, DocumentListItem[]>);
  }, [documents]);

  const allDocuments = useMemo(
    () => CATEGORIES.flatMap(({ value }) => byCategory[value]),
    [byCategory]
  );

  const visibleDocuments =
    filter === "all" ? allDocuments : byCategory[filter] ?? [];

  const groups = useMemo(
    () =>
      CATEGORIES.map(({ value, label }) => ({
        title: label,
        documents: byCategory[value],
      })),
    [byCategory]
  );

  const handleDownload = useCallback(
    async (document: DocumentListItem) => {
      try {
        await downloadDocument(
          document.media as string,
          authHeader,
          document.fileName
        );
      } catch {
        notify("There was a problem while downloading the document", {
          position: "bottom-left",
          type: "error",
        });
      }
    },
    [authHeader]
  );

  const filterCount = (value: FilterKey) =>
    value === "all" ? allDocuments.length : byCategory[value].length;

  return (
    <>
      <Slider isOpen={isOpen} onRequestClose={onRequestClose} width="720px">
        {documentsLoading ? (
          <Flex justifyContent="center" alignItems="center" minHeight="60vh">
            <Loader />
          </Flex>
        ) : (
          <Box height="100%" overflowY="auto">
            <PanelHeader>
              <Flex alignItems="baseline" gap="8" mb={2} mr={4}>
                <H2 mb="0px">Uploaded Documents</H2>
                <Paragraph fontSize={1} color="gray.50" my={0}>
                  {allDocuments.length}
                </Paragraph>
              </Flex>

              <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="12"
                flexWrap="wrap"
              >
                <Flex gap="6" flexWrap="wrap">
                  {(
                    [{ value: "all", label: "All" }, ...CATEGORIES] as {
                      value: FilterKey;
                      label: string;
                    }[]
                  ).map(({ value, label }) => (
                    <FilterPill
                      key={value}
                      type="button"
                      isActive={filter === value}
                      onClick={() => setFilter(value)}
                    >
                      {label}
                      <PillCount isActive={filter === value}>
                        {filterCount(value)}
                      </PillCount>
                    </FilterPill>
                  ))}
                </Flex>

                <Flex gap="8" alignItems="center">
                  {/* Requesting a document is not a property of the filter you
                      happen to be looking at, so this stays put rather than
                      appearing only under Additional Documents. */}
                  <Button
                    variant="secondary"
                    onClick={() => setIsSelectAdditionalDocumentOpen(true)}
                  >
                    <Paragraph
                      fontWeight="500"
                      fontSize={1}
                      color="blue.primary"
                    >
                      Request New
                    </Paragraph>
                  </Button>

                  <Button
                    variant="secondary"
                    isDisabled={!visibleDocuments.length}
                    onClick={() =>
                      downloadDocuments(visibleDocuments, authHeader)
                    }
                  >
                    <Image
                      src={downloadIcon}
                      alt="Download All Documents"
                      height="auto"
                      width="22px"
                      mr={1}
                    />
                    <Paragraph
                      fontWeight="500"
                      fontSize={1}
                      color="blue.primary"
                    >
                      Download All
                    </Paragraph>
                  </Button>
                </Flex>
              </Flex>
            </PanelHeader>

            <Box px={3} pb={4}>
              <DocumentList
                showType={false}
                emptyState={
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    margin="15% auto"
                    gap="8"
                  >
                    <Image
                      src={noDocuments}
                      alt="No documents available"
                      height="auto"
                    />
                    <H2 mb="0px">No documents available</H2>
                  </Flex>
                }
                onDownload={handleDownload}
                {...(filter === "all"
                  ? { groups }
                  : { documents: visibleDocuments })}
              />
            </Box>
          </Box>
        )}
      </Slider>
      <NewDocumentsRequest
        isOpen={isSelectAdditionalDocumentOpen}
        onRequestClose={() => setIsSelectAdditionalDocumentOpen(false)}
        includedAdditionalDocument={includedAdditionalDocument}
        mandateId={mandateId}
        individualId={individualId}
        documentType={resolvedDocumentType}
      />
    </>
  );
};
